import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, AlertCircle, RefreshCw, Table, Code } from 'lucide-react';
import TableBrowser from './TableBrowser';
import TableViewer from './TableViewer';
import QueryEditor from './QueryEditor';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const DatabaseAdmin = () => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [activeTab, setActiveTab] = useState('tables'); // 'tables' or 'query'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/api/admin/database/tables`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setTables(response.data.tables);
    } catch (err) {
      console.error('Error fetching tables:', err);
      setError(err.response?.data?.message || 'Failed to load tables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const handleTableSelect = (table) => {
    setSelectedTable(table);
  };

  const handleBackToTables = () => {
    setSelectedTable(null);
    fetchTables(); // Refresh table list
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedTable(null); // Reset selected table when switching tabs
  };

  if (loading && activeTab === 'tables') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Database Admin</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={fetchTables}
              className="mt-3 inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Database className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Database Administration</h2>
              <p className="mt-1 text-sm text-gray-500">
                Direct database access with audit logging and safety controls
              </p>
            </div>
          </div>
          <button
            onClick={fetchTables}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('tables')}
              className={`${
                activeTab === 'tables'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Table className="h-5 w-5 mr-2" />
              Table Browser
            </button>
            <button
              onClick={() => handleTabChange('query')}
              className={`${
                activeTab === 'query'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Code className="h-5 w-5 mr-2" />
              SQL Query Editor
            </button>
          </nav>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
            <p className="mt-1 text-sm text-yellow-700">
              All database modifications are logged in the audit trail. Sensitive fields (passwords, tokens) are hidden.
              Destructive operations require confirmation.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'tables' ? (
        selectedTable ? (
          <TableViewer
            table={selectedTable}
            onBack={handleBackToTables}
          />
        ) : (
          <TableBrowser
            tables={tables}
            onSelectTable={handleTableSelect}
          />
        )
      ) : (
        <QueryEditor />
      )}
    </div>
  );
};

export default DatabaseAdmin;
